import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import apiClient from '../utils/api';

const GuestApplyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const GuestApplyCard = styled.div`
  background: white;
  padding: 3rem 2.5rem;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 480px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
    border-radius: 20px;
    max-width: 100%;
  }
`;

const Title = styled.h1`
  color: #000000;
  margin-bottom: 1.5rem;
  font-size: 2rem;
  text-align: center;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.25rem;
  }
`;

const Notice = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 2rem;
  text-align: left;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const NoticeTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #856404;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const NoticeText = styled.div`
  font-size: 0.875rem;
  color: #856404;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  text-align: left;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const ConsentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ConsentLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.75rem;
  display: block;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const ConsentBox = styled.div`
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  max-height: 120px;
  overflow-y: auto;
  background: #f8f9fa;
  margin-bottom: 0.75rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #e9ecef;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #adb5bd;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #6c757d;
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    max-height: 100px;
  }
`;

const ConsentText = styled.div`
  font-size: 0.8125rem;
  color: #495057;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-bottom: 0.5rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-top: 2px;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9375rem;
  color: #343a40;
  cursor: pointer;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
`;

const RadioRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const RadioLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.75rem;
  display: block;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const RadioOptions = styled.div`
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    gap: 1.25rem;
  }
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
  color: #343a40;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #000000;
`;

const CalendarSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`;

const CalendarLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px solid #dee2e6;
  
  @media (max-width: 768px) {
    gap: 0.375rem;
    padding: 0.75rem;
  }
`;

const CalendarHeader = styled.div`
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  padding: 0.5rem 0;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
    padding: 0.375rem 0;
  }
`;

const CalendarDate = styled.button<{ isAvailable?: boolean; isSelected?: boolean; isDisabled?: boolean }>`
  aspect-ratio: 1;
  border: 2px solid ${props => props.isSelected ? '#000000' : props.isAvailable ? '#000000' : '#dee2e6'};
  background: ${props => props.isSelected ? '#000000' : props.isAvailable ? '#ffffff' : '#f8f9fa'};
  color: ${props => props.isSelected ? '#ffffff' : props.isAvailable ? '#000000' : '#adb5bd'};
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: ${props => props.isSelected ? '700' : '500'};
  cursor: ${props => props.isAvailable ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    ${props => props.isAvailable && !props.isSelected && `
      background: #e9ecef;
      transform: scale(1.05);
    `}
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
    border-width: 1.5px;
  }
`;

const CalendarMonth = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  font-size: 1rem;
  font-weight: 700;
  color: #000000;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const CalendarInfo = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 0.5rem;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const DateSelectButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  background: white;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.9375rem;
  color: ${props => props.value ? '#000000' : '#6c757d'};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    border-color: #000000;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.875rem;
  }
`;

const DateSelectIcon = styled.span`
  font-size: 1.25rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #000000;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.625rem;
  }
`;

const SuccessMessage = styled.div`
  color: #0f5132;
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #d1e7dd;
  border: 1px solid #badbcc;
  border-radius: 8px;
  text-align: center;
  white-space: pre-line;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.625rem;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  font-size: 0.875rem;
  cursor: pointer;
  margin-top: 1rem;
  text-decoration: underline;
  
  &:hover {
    color: #000000;
  }
`;

const GuestApply: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    birthDate: '',
    gender: '',
    electionStatus: '',
    selectedDate: '',
    applyPath: ''
  });
  const [agreed, setAgreed] = useState(false);
  const [filmingAgreed, setFilmingAgreed] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [schedules, setSchedules] = useState<Array<{ schedule_date: string }>>([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  
  // 운동일정 목록 조회
  const fetchSchedules = async () => {
    try {
      setLoadingSchedules(true);
      const response = await apiClient.get('/schedules');
      if (response.data.success) {
        setSchedules(response.data.data || []);
      }
    } catch (error) {
      console.error('운동일정 조회 실패:', error);
      setSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  // API에서 가져온 일정 날짜 목록
  const availableDates = schedules.map(s => s.schedule_date);
  
  // 달력 생성 (현재 월 기준)
  const generateCalendar = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const calendar = [];
    
    // 빈 칸 채우기 (월 시작 전)
    for (let i = 0; i < startDayOfWeek; i++) {
      calendar.push(null);
    }
    
    // 오늘 날짜 (시간 제거)
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // 날짜 채우기
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isPast = dateStr < todayStr;
      calendar.push({
        day,
        dateStr,
        isAvailable: availableDates.includes(dateStr) && !isPast
      });
    }
    
    return {
      year,
      month: month + 1,
      calendar
    };
  };
  
  const { year, month, calendar } = generateCalendar();

  const formatPhoneNumber = (value: string) => {
    // 숫자만 추출
    const numbers = value.replace(/[^\d]/g, '');
    
    // 최대 11자리까지만
    const limited = numbers.slice(0, 11);
    
    // 3-4-4 형식으로 포맷팅
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 7) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
    }
  };

  const handleInputChange = (field: string, value: string) => {
    let formattedValue = value;
    
    // 전화번호 필드인 경우 자동 포맷팅
    if (field === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    // 생년월일: 숫자만 입력, 6자리 제한
    if (field === 'birthDate') {
      const numbers = value.replace(/[^\d]/g, '');
      formattedValue = numbers.slice(0, 6);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 유효성 검사
    if (!formData.name.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    
    if (!formData.phone.trim()) {
      setError('전화번호를 입력해주세요.');
      return;
    }
    
    if (!formData.birthDate.trim()) {
      setError('생년월일을 입력해주세요.');
      return;
    }
    
    if (formData.birthDate.length !== 6) {
      setError('생년월일은 6자리로 입력해주세요. (예: 231105)');
      return;
    }
    
    if (!formData.gender) {
      setError('성별을 선택해주세요.');
      return;
    }
    
    if (!formData.electionStatus) {
      setError('선출 여부를 선택해주세요.');
      return;
    }
    
    if (!formData.selectedDate) {
      setError('참가 희망일을 선택해주세요.');
      return;
    }
    
    if (!filmingAgreed) {
      setError('촬영 및 SNS 게시 동의를 확인해주세요.');
      return;
    }
    
    if (!agreed) {
      setError('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.post('/guests', {
        name: formData.name,
        phone: formData.phone,
        birth_date: formData.birthDate,
        gender: formData.gender,
        election_status: formData.electionStatus,
        selected_date: formData.selectedDate,
        invited_by_user_no: 0, // Home에서 직접 신청 (비회원)
        apply_path: formData.applyPath || null // 신청 경로
      });
      
      if (response.data.success) {
        const guestData = response.data.data;
        const applyCount = guestData.apply_count || 1;
        const feeRequired = guestData.fee_required || false;
        
        let successMessage = '게스트 신청이 완료되었습니다!';
        if (feeRequired) {
          successMessage += `\n\n이번 신청은 ${applyCount}회차로, 게스트 비용 8,000원이 필요합니다.`;
        } else {
          successMessage += `\n\n이번 신청은 ${applyCount}회차로 무료입니다.`;
        }
        
        setSuccess(successMessage);
        setTimeout(() => navigate('/'), 3000);
      } else {
        setError('신청에 실패했습니다.');
      }
    } catch (err: any) {
      console.error('게스트 신청 오류:', err);
      
      // API 에러 응답에서 메시지 추출
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <GuestApplyContainer>
      <GuestApplyCard>
        <Title>H_B 게스트 신청</Title>
        
        <Notice>
          <NoticeTitle>
            ⚠️ 안내사항
          </NoticeTitle>
          <NoticeText>
            2회차부터 게스트 비용이 발생합니다. 
            <br />
            1회 참가는 무료이며, 2회차부터는 회당 8,000원의 게스트 비용이 발생합니다.
            <br />
            조원고등학교 체육관 09시부터 13시까지 운동합니다.
          </NoticeText>
        </Notice>
        
        <Form onSubmit={handleSubmit}>
          <Input
            label="이름"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="이름을 입력하세요"
            required
          />
          
          <Input
            label="전화번호"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="010-0000-0000"
            required
          />
          
          <Input
            label="생년월일"
            type="text"
            value={formData.birthDate}
            onChange={(e) => handleInputChange('birthDate', e.target.value)}
            placeholder="6자리 (예: 231105)"
            required
          />
          
          <RadioRow>
            <RadioGroup>
              <RadioLabel>성별 <span style={{ color: '#dc3545' }}>*</span></RadioLabel>
              <RadioOptions>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="gender"
                    value="남자"
                    checked={formData.gender === '남자'}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  />
                  남자
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="gender"
                    value="여자"
                    checked={formData.gender === '여자'}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  />
                  여자
                </RadioOption>
              </RadioOptions>
            </RadioGroup>
            
            <RadioGroup>
              <RadioLabel>선출 여부 <span style={{ color: '#dc3545' }}>*</span></RadioLabel>
              <RadioOptions>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="electionStatus"
                    value="선출"
                    checked={formData.electionStatus === '선출'}
                    onChange={(e) => handleInputChange('electionStatus', e.target.value)}
                  />
                  선출
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="electionStatus"
                    value="비선출"
                    checked={formData.electionStatus === '비선출'}
                    onChange={(e) => handleInputChange('electionStatus', e.target.value)}
                  />
                  비선출
                </RadioOption>
              </RadioOptions>
            </RadioGroup>
          </RadioRow>
          
          <CalendarSection>
            <CalendarLabel>참가 희망일 <span style={{ color: '#dc3545' }}>*</span></CalendarLabel>
            <DateSelectButton
              type="button"
              onClick={() => setIsCalendarOpen(true)}
              value={formData.selectedDate}
            >
              <span>
                {formData.selectedDate 
                  ? `${formData.selectedDate} 선택됨` 
                  : '날짜를 선택해주세요'}
              </span>
              <DateSelectIcon>📅</DateSelectIcon>
            </DateSelectButton>
          </CalendarSection>
          
          {/* 달력 모달 */}
          <ModalOverlay 
            isOpen={isCalendarOpen} 
            onClick={() => setIsCalendarOpen(false)}
          >
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>참가 희망일 선택</ModalTitle>
                <CloseButton onClick={() => setIsCalendarOpen(false)}>
                  ×
                </CloseButton>
              </ModalHeader>
              
              {loadingSchedules ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                  일정을 불러오는 중...
                </div>
              ) : (
                <>
                  <CalendarGrid>
                    <CalendarMonth>{year}년 {month}월</CalendarMonth>
                    
                    {['일', '월', '화', '수', '목', '금', '토'].map(day => (
                      <CalendarHeader key={day}>{day}</CalendarHeader>
                    ))}
                    
                    {calendar.map((date, index) => {
                      if (!date) {
                        return <div key={`empty-${index}`} />;
                      }
                      
                      return (
                        <CalendarDate
                          key={date.dateStr}
                          type="button"
                          isAvailable={date.isAvailable}
                          isSelected={formData.selectedDate === date.dateStr}
                          isDisabled={!date.isAvailable}
                          disabled={!date.isAvailable}
                          onClick={(e) => {
                            e.preventDefault();
                            if (date.isAvailable) {
                              // 오늘 이전 날짜 체크
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const selectedDate = new Date(date.dateStr);
                              selectedDate.setHours(0, 0, 0, 0);
                              
                              if (selectedDate < today) {
                                alert('오늘보다 이전 날짜는 선택할 수 없습니다.');
                                return;
                              }
                              
                              handleInputChange('selectedDate', date.dateStr);
                            }
                          }}
                        >
                          {date.day}
                        </CalendarDate>
                      );
                    })}
                  </CalendarGrid>
                  
                  <CalendarInfo>
                    {availableDates.length > 0 
                      ? '✓ 검은색 테두리 날짜만 선택 가능합니다.'
                      : '등록된 운동 일정이 없습니다. 관리자에게 문의해주세요.'}
                  </CalendarInfo>
                </>
              )}
              
              <ModalActions>
                <Button
                  type="button"
                  variant="primary"
                  size="large"
                  fullWidth
                  onClick={() => setIsCalendarOpen(false)}
                  disabled={!formData.selectedDate}
                >
                  선택 완료
                </Button>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
          
          <Input
            label="신청 경로"
            type="text"
            value={formData.applyPath}
            onChange={(e) => handleInputChange('applyPath', e.target.value)}
            placeholder="오픈카톡, 지인이름"
          />
          
          <ConsentSection>
            <ConsentLabel>촬영 및 SNS 게시 동의</ConsentLabel>
            <ConsentBox>
              <ConsentText>
                H_B는 매주 훈련 및 경기 영상을 촬영하고 있습니다.
                <br /><br />
                촬영된 사진과 영상은 <a 
                  href="https://www.instagram.com/hb_volleyball_club?igsh=Ymh2bGRhZHA1Zm0x&utm_source=qr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#000000', fontWeight: '600', textDecoration: 'underline' }}
                >
                    <br />
                  H_B 인스타그램
                </a>
                <br />에 게시될 수 있으며,
                
                홍보 및 활동 기록 목적으로만 사용됩니다.
                <br />
                촬영에 동의하지 않을 경우, 게스트 참여가 제한될 수 있으니
                사전에 양해 부탁드립니다.
              </ConsentText>
            </ConsentBox>
            
            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                id="filming"
                checked={filmingAgreed}
                onChange={(e) => setFilmingAgreed(e.target.checked)}
              />
              <CheckboxLabel htmlFor="filming">
                촬영 및 SNS 게시에 동의합니다 (필수)
              </CheckboxLabel>
            </CheckboxWrapper>
          </ConsentSection>
          
          <ConsentSection>
            <ConsentLabel>개인정보 수집 및 이용 동의</ConsentLabel>
            <ConsentBox>
              <ConsentText>
              <strong>수집 목적:</strong> H_B 배구동호회의 게스트 관리 및 운영
                <br /><br />
                <strong>수집 항목:</strong> 이름, 전화번호, 생년월일, 성별, 선출 여부, 참가 희망일
                <br /><br />
                <strong>보유 기간:</strong> 영구 보관
                <br /><br />
                귀하는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있으며, 
                동의하지 않을 경우 게스트 신청이 제한될 수 있습니다.
                <br /><br />
                수집된 개인정보는 동호회 회원 관리 및 연락 목적으로만 사용되며, 
                제3자에게 제공되지 않습니다. 또한, 2회차 이후부터는 게스트 비용이 발생하므로, 
                참가 횟수 확인을 위해 이름과 전화번호를 보관합니다.
              </ConsentText>
            </ConsentBox>
            
            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                id="consent"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <CheckboxLabel htmlFor="consent">
                개인정보 수집 및 이용에 동의합니다 (필수)
              </CheckboxLabel>
            </CheckboxWrapper>
          </ConsentSection>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={loading}
          >
            {loading ? '신청 중...' : '게스트 신청하기'}
          </Button>
        </Form>
        
        <BackButton onClick={() => navigate('/')}>
          돌아가기
        </BackButton>
      </GuestApplyCard>
    </GuestApplyContainer>
  );
};

export default GuestApply;

